import type { AppShellIconKey } from "../../../../../../shared/frontend/layout/appShellIcons";
import { Permissions } from "../../app/constants/permissions";
import type { LocalizedText } from "../../app/i18n/i18n";
import type { AreaAccessContext } from "../../app/navigation/areaCatalog";
import { permissionRow, rowPermissionKeys, type PermissionRowKey } from "../../app/permissions/permissionRows";

const AUTOMATION_ROW: PermissionRowKey = "automation";

export type ToolsCatalogItem = {
  title: string;
  titleKey?: LocalizedText;
  description: string;
  descriptionKey?: LocalizedText;
  path: string;
  iconKey: string;
  menuIcon: AppShellIconKey;
  /** Rechtezeile des Rolleneditors; ihre Stufen und Sonderaktionen öffnen die Seite. */
  row: PermissionRowKey;
  /** Wenn gesetzt, wird das Item nur angezeigt, wenn die Tenant-Capability aktiv ist. */
  requiredCapability?: string;
};

export type ToolsCatalogGroup = {
  title: string;
  titleKey?: LocalizedText;
  items: ToolsCatalogItem[];
};

export const toolsCatalogGroups: ToolsCatalogGroup[] = [
  {
    title: "Werkzeuge",
    titleKey: "Tools",
    items: [
      {
        title: "Automatisierung",
        titleKey: "Automation",
        iconKey: "ThunderboltOutlined",
        menuIcon: "ThunderboltOutlined",
        description: "Regeln & Protokoll für Kontakte, Auto-Kommunikation und weitere Automatisierungen",
        descriptionKey: "Rules & log for leads, auto-communication and other automation",
        path: "/tools/automation",
        requiredCapability: "automation",
        row: AUTOMATION_ROW
      },
      {
        title: "Aktionen & Boni",
        titleKey: "Promotions & bonuses",
        iconKey: "GiftOutlined",
        menuIcon: "GiftOutlined",
        description: "Empfehlungen, Bonusfälle und Einlösungen verwalten",
        descriptionKey: "Manage referrals, bonus cases and redemptions",
        path: "/tools/member-promotions",
        requiredCapability: "memberPromotions",
        row: "memberPromotions"
      },
      {
        title: "Duplikatfinder",
        titleKey: "Duplicate finder",
        iconKey: "MergeCellsOutlined",
        menuIcon: "MergeCellsOutlined",
        description: "Doppelte Personendaten finden und zusammenführen",
        descriptionKey: "Find and merge duplicate person records",
        path: "/tools/duplicate-finder",
        row: "duplicateFinder"
      }
    ]
  }
];

/** Schlüssel, von denen einer die Werkzeugseite öffnet (Stufen und Sonderaktionen der Zeile). */
export function toolsItemPermissions(item: ToolsCatalogItem): string[] {
  return rowPermissionKeys(permissionRow(item.row));
}

/**
 * Recht für ein Werkzeug. Automationen verlangen zusätzlich mindestens einen Bereich
 * (Mitglieder, Finanzen, Verträge, Betrieb), außer bei „bearbeiten“ — sonst zeigt die Seite nichts.
 */
export function hasToolsItemPermission(item: ToolsCatalogItem, ctx: Pick<AreaAccessContext, "hasAnyPermission">): boolean {
  if (!ctx.hasAnyPermission(...toolsItemPermissions(item))) return false;
  if (item.row !== AUTOMATION_ROW) return true;
  return ctx.hasAnyPermission(Permissions.AutomationManage)
    || ctx.hasAnyPermission(Permissions.AutomationMembers, Permissions.AutomationFinance, Permissions.AutomationContracts, Permissions.AutomationOperations);
}

export function isToolsItemVisible(item: ToolsCatalogItem, ctx: AreaAccessContext): boolean {
  if (item.requiredCapability && !ctx.capabilityEnabled(item.requiredCapability)) return false;
  return hasToolsItemPermission(item, ctx);
}

export function toolsItemForPath(path: string): ToolsCatalogItem {
  const item = toolsCatalogGroups.flatMap((g) => g.items).find((i) => i.path === path);
  if (!item) throw new Error(`Kein Werkzeug-Katalogeintrag für ${path}`);
  return item;
}

const titleByPath: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const g of toolsCatalogGroups) {
    for (const item of g.items || []) {
      if (!item?.path) continue;
      const key = typeof item.titleKey === "string" ? item.titleKey : item.title;
      map[String(item.path)] = key;
    }
  }
  return map;
})();

const iconKeyByPath: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const g of toolsCatalogGroups) {
    for (const item of g.items || []) {
      if (!item?.path || !item.iconKey) continue;
      map[String(item.path)] = item.iconKey;
    }
  }
  return map;
})();

export function toolsTitleForPath(pathname: string): string | null {
  const p = String(pathname || "");
  return titleByPath[p] || null;
}

/** Symbol-Schlüssel der Werkzeugseite — damit Pins dasselbe Symbol tragen wie die Kachel. */
export function toolsIconKeyForPath(pathname: string): string | null {
  const p = String(pathname || "");
  return iconKeyByPath[p] || null;
}
